/**
 * Ryan O'Dowd
 * 2022-03-13
 * © Copyright 2022 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  applyMiddleware,
  createStore, // @TODO: createStore is deprecated in favor of redux toolkit: https://redux.js.org/usage/migrating-to-modern-redux; wait...according to the docs, "createStore will continue to work indefinitely, and will not ever be removed. But, today we want all Redux users to be using Redux Toolkit for all of their Redux logic."
} from 'redux';
import {
  createMigrate,
  persistReducer,
  persistStore,
} from 'redux-persist';
import {
  apiMiddleware,
} from 'redux-api-middleware';
import {
  channelIds,
} from '@utils/globals';
import rootReducer from './reducers';
import storage from './storage';
import {
  thunk,
} from 'redux-thunk';

const migrations = {
  1: (state) => {
    // adding "what's new" notification preference
    const newState = {...state};
    if (newState.settingsNotificationPreferences) {
      newState.settingsNotificationPreferences[channelIds.WHATS_NEW] = 1;
    }
    return newState;
  },
  2: (state) => {
    // verses reducer now has objects of shape {verseId, verseText, heading, footnotes} at the leaf node for verseNumber instead of raw text only
    const newState = {...state};
    newState.verses = {}; // it would be better to see which verses are already downloaded and to refetch them, but this is way easier and less error prone
    newState.wrinkles = {}; // used to be an array...keying by verseId is going to be better overall
    return newState;
  },
  3: (state) => {
    // introducting modules instead of passages in goals
    const newState = {...state};
    newState.goals = {};
    return newState;
  },
  4: (state) => {
    // combining goals and classroom_goal_metadata
    const newState = {...state};
    newState.goals = {};
    newState.goalProgress = {};
    return newState;
  },
  5: (state) => {
    // settingsShowBadgesInRead should be true by default
    const newState = {...state};
    newState.settingsShowBadgesInRead = true;
    return newState;
  },
};

const persistConfig = {
  key: 'wrinklyRoot',
  version: 5, // @NOTE: only change this when the store should be wiped before rehydrated (or there's a migration); incrementing this is better than having users need to uninstall and reinstall because the reducer shape has changed
  storage,
  blacklist: [
    'fetches',
    'walkthrough', // @TODO: is this still used?
    'peanutsSevenDays',
    'loadingStates',
    'newTranslationsReturnValue', // @TODO: is this still used?
    'versionSupport',
    'goalPreviews',
    'publicUsersSearched',
  ],
  migrate: createMigrate(migrations, {debug: false}),
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = createStore(persistedReducer, {}, applyMiddleware(thunk, apiMiddleware));
const configureStore = () => {
  const persistor = persistStore(store);

  // @NOTE: uncomment when store needs reset; IMPORTANT: do NOT leave uncommented during a deployment
  // persistor.purge();

  return {
    store,
    persistor,
  };
};

export type AppDispatch = typeof store.dispatch;

export default configureStore;
